import type { CalculatedColumn, Column, Position, CellNavigationMode } from '../types';

interface IsSelectedCellEditableOpts<R, SR> {
  column: Column<R, SR>;
  row: R;
}

export function isCellEditable<R, SR>({ column, row }: IsSelectedCellEditableOpts<R, SR>): boolean {
  return (
    column?.editor != null &&
    (typeof column?.editable === 'function' ? column?.editable(row) : column?.editable) !== false
  );
}

interface GetNextSelectedCellPositionOpts<R, SR> {
  cellNavigationMode: CellNavigationMode;
  columns: readonly CalculatedColumn<R, SR>[];
  rows: readonly R[];
  nextPosition: Position;
}

export function getNextSelectedCellPosition<R, SR>({
  cellNavigationMode,
  columns,
  rows,
  nextPosition,
}: GetNextSelectedCellPositionOpts<R, SR>): Position {
  const rowsCount = rows.length;
  let position = nextPosition;

  if (cellNavigationMode !== 'NONE') {
    const { columnIdx, rowIdx } = nextPosition;
    const columnsCount = columns.length;
    const isAfterLastColumn = columnIdx === columnsCount;
    const isBeforeFirstColumn = columnIdx === -1;

    if (isAfterLastColumn) {
      if (cellNavigationMode === 'CHANGE_ROW') {
        const isLastRow = rowIdx === rowsCount - 1;
        if (!isLastRow) {
          position = {
            columnIdx: 0,
            rowIdx: rowIdx + 1,
          };
        }
      } else {
        position = {
          rowIdx,
          columnIdx: 0,
        };
      }
    } else if (isBeforeFirstColumn) {
      if (cellNavigationMode === 'CHANGE_ROW') {
        const isFirstRow = rowIdx === 0;
        if (!isFirstRow) {
          position = {
            rowIdx: rowIdx - 1,
            columnIdx: columnsCount - 1,
          };
        }
      } else {
        position = {
          rowIdx,
          columnIdx: columnsCount - 1,
        };
      }
    }
  }

  return position;
}

interface CanExitGridOpts<R, SR> {
  cellNavigationMode: CellNavigationMode;
  columns: readonly CalculatedColumn<R, SR>[];
  rowsCount: number;
  selectedRange: Position;
  shiftKey: boolean;
}

export function canExitGrid<R, SR>({
  cellNavigationMode,
  columns,
  rowsCount,
  selectedRange: { rowIdx, columnIdx },
  shiftKey,
}: CanExitGridOpts<R, SR>): boolean {
  // When the cellNavigationMode is 'none' or 'changeRow',
  // you can exit the grid if you're at the first or last cell of the grid
  // When the cellNavigationMode is 'loopOverRow', there is no logical exit point so you can't exit the grid
  if (cellNavigationMode === 'NONE' || cellNavigationMode === 'CHANGE_ROW') {
    const atLastCellInRow = columnIdx === columns.length - 1;
    const atFirstCellInRow = columnIdx === 0;
    const atLastRow = rowIdx === rowsCount - 1;
    const atFirstRow = rowIdx === 0;

    return shiftKey ? atFirstCellInRow && atFirstRow : atLastCellInRow && atLastRow;
  }

  return false;
}
